import { Box, Button, Typography } from '@mui/material';
import {
  PlaceOutlined,
  BadgeOutlined,
  ExploreOutlined,
  MyLocationOutlined,
  WorkspacePremiumOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { useT } from '../i18n/useT';
import { StringKey } from '../i18n/strings';

const sections: Array<{ icon: typeof PlaceOutlined; titleKey: StringKey; bodyKey: StringKey }> = [
  { icon: BadgeOutlined, titleKey: 'visiInfoWhatTitle', bodyKey: 'visiInfoWhatBody' },
  { icon: PlaceOutlined, titleKey: 'visiInfoMeVsVisiTitle', bodyKey: 'visiInfoMeVsVisiBody' },
  { icon: MyLocationOutlined, titleKey: 'visiInfoLocationTitle', bodyKey: 'visiInfoLocationBody' },
  { icon: ExploreOutlined, titleKey: 'visiInfoDiscoveryTitle', bodyKey: 'visiInfoDiscoveryBody' },
];

const steps: Array<{ titleKey: StringKey; bodyKey: StringKey }> = [
  { titleKey: 'visiInfoStep1Title', bodyKey: 'visiInfoStep1Body' },
  { titleKey: 'visiInfoStep2Title', bodyKey: 'visiInfoStep2Body' },
  { titleKey: 'visiInfoStep3Title', bodyKey: 'visiInfoStep3Body' },
];

export default function VisiInfoPage() {
  const navigate = useNavigate();
  const t = useT();

  return (
    <AppShell title={t('visiInfoNavTitle')} showBackButton maxWidth="md" showCompanion={false}>
      <Box
        sx={{
          p: { xs: 2.5, md: 3.5 },
          mb: 2.5,
          borderRadius: DesignSystem.borderRadius.lg,
          backgroundColor: DesignSystem.colors.surface,
        }}
      >
        <Typography
          sx={{
            ...DesignSystem.typography.sectionLabel,
            color: DesignSystem.colors.primary,
            mb: 1.25,
          }}
        >
          {t('visiInfoHeroKicker')}
        </Typography>
        <Typography
          component="h2"
          sx={{
            fontSize: { xs: 26, md: 32 },
            fontWeight: 750,
            lineHeight: 1.15,
            letterSpacing: '-0.03em',
            color: DesignSystem.colors.textPrimary,
            mb: 1.5,
          }}
        >
          {t('visiInfoHeroTitle')}
        </Typography>
        <Typography
          sx={{
            fontSize: 16,
            lineHeight: 1.55,
            color: DesignSystem.colors.textSecondary,
          }}
        >
          {t('visiInfoHeroBody')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {sections.map(({ icon: Icon, titleKey, bodyKey }) => (
          <Box
            key={titleKey}
            sx={{
              p: 2.5,
              borderRadius: DesignSystem.borderRadius.lg,
              backgroundColor: DesignSystem.colors.surface,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                mb: 1.5,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: DesignSystem.colors.primaryLight,
              }}
            >
              <Icon sx={{ fontSize: 20, color: DesignSystem.colors.primary }} />
            </Box>
            <Typography
              component="h3"
              sx={{
                fontSize: 17,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: DesignSystem.colors.textPrimary,
                mb: 0.75,
              }}
            >
              {t(titleKey)}
            </Typography>
            <Typography sx={{ fontSize: 14.5, lineHeight: 1.5, color: DesignSystem.colors.textSecondary }}>
              {t(bodyKey)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 2.5,
          borderRadius: DesignSystem.borderRadius.lg,
          backgroundColor: DesignSystem.colors.surface,
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: DesignSystem.colors.textPrimary,
            mb: 2,
          }}
        >
          {t('visiInfoStepsTitle')}
        </Typography>
        {steps.map((step, i) => (
          <Box
            key={step.titleKey}
            sx={{
              display: 'flex',
              gap: 1.75,
              pb: i === steps.length - 1 ? 0 : 2,
              mb: i === steps.length - 1 ? 0 : 2,
              borderBottom: i === steps.length - 1 ? 'none' : `1px solid ${DesignSystem.colors.separator}`,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: DesignSystem.colors.onPrimary,
                backgroundColor: DesignSystem.colors.action,
              }}
            >
              {i + 1}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 650,
                  color: DesignSystem.colors.textPrimary,
                  mb: 0.4,
                }}
              >
                {t(step.titleKey)}
              </Typography>
              <Typography sx={{ fontSize: 14.5, lineHeight: 1.5, color: DesignSystem.colors.textSecondary }}>
                {t(step.bodyKey)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.75,
          p: 2.5,
          mb: 2.5,
          borderRadius: DesignSystem.borderRadius.lg,
          backgroundColor: DesignSystem.colors.surface,
        }}
      >
        <WorkspacePremiumOutlined sx={{ fontSize: 22, color: DesignSystem.colors.primary, flexShrink: 0 }} />
        <Box>
          <Typography
            component="h3"
            sx={{ fontSize: 17, fontWeight: 700, color: DesignSystem.colors.textPrimary, mb: 0.6 }}
          >
            {t('visiInfoSubscriptionTitle')}
          </Typography>
          <Typography sx={{ fontSize: 14.5, lineHeight: 1.5, color: DesignSystem.colors.textSecondary }}>
            {t('visiInfoSubscriptionBody')}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          borderRadius: DesignSystem.borderRadius.lg,
          backgroundColor: DesignSystem.colors.surfaceElevated,
          textAlign: 'center',
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize: 22,
            fontWeight: 750,
            letterSpacing: '-0.02em',
            color: DesignSystem.colors.textPrimary,
            mb: 0.75,
          }}
        >
          {t('visiInfoCtaTitle')}
        </Typography>
        <Typography sx={{ fontSize: 15, color: DesignSystem.colors.textSecondary, mb: 2 }}>
          {t('visiInfoCtaBody')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/visi/create')}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            px: 3,
            py: 1.15,
            boxShadow: 'none',
            borderRadius: DesignSystem.borderRadius.pill,
            backgroundColor: DesignSystem.colors.action,
            color: DesignSystem.colors.onPrimary,
            '&:hover': { backgroundColor: DesignSystem.colors.actionDark, boxShadow: 'none' },
          }}
        >
          {t('visiInfoCtaButton')}
        </Button>
      </Box>
    </AppShell>
  );
}
